import React, { useState } from "react";
import { LuCalendar } from "react-icons/lu";
import { TbChevronLeft, TbChevronRight } from "react-icons/tb";
// @ts-ignore
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePickerWrapper.css";
import { useParams } from "react-router-dom";
import * as translator from "../../utils/translator";
// date-fns v2 ships locales as default exports.
// @ts-ignore
import de from "date-fns/locale/de";
// @ts-ignore
import enGB from "date-fns/locale/en-GB";
// @ts-ignore
import fr from "date-fns/locale/fr";

// utils
import * as timestampFormatter from "../../utils/timestampFormatter";

// Weekday headers are rendered by react-datepicker itself, so without a
// registered locale they always come out in English. Registered once at module
// level; only applied when a caller opts in via `localizeWeekdays`.
registerLocale("de", de);
registerLocale("en", enGB);
registerLocale("fr", fr);

const SUPPORTED_CALENDAR_LOCALES = ["de", "en", "fr"];

type NavArrowProps = {
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
  variant: "default" | "appointment";
};

function NavArrow({ direction, disabled, onClick, variant }: NavArrowProps) {
  const Icon = direction === "prev" ? TbChevronLeft : TbChevronRight;

  return (
    <button
      type="button"
      className={`date-picker-arrow ${variant === "appointment" ? "date-picker-arrow-appointment" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === "prev" ? "Previous month" : "Next month"}
    >
      <Icon className="date-picker-arrow-icon" aria-hidden="true" />
    </button>
  );
}

type DateInputProps = {
  onChange: Function;
  id?: string;
  errorMode: boolean;
  value?: string;
  isBirthdayInput?: boolean;
  inline?: boolean;
  isBodyLevelPortal?: boolean;
  customDayClassName?: (date: Date) => string | undefined;
  variant?: "default" | "appointment"; // New prop for different visual styles
  minDate?: Date | null;
  /**
   * Upper bound for selectable dates. Only applies when `isBirthdayInput` is false — birthday
   * inputs derive their own max from the 18-year rule.
   */
  maxDate?: Date | null;
  filterDate?: (date: Date) => boolean;
  disabled?: boolean;
  placeholderText?: string;
  // Opt-in: render weekday headers in the active language and shield them from
  // Google Translate. Off by default so existing callers are unaffected.
  localizeWeekdays?: boolean;
  // Opt-in: with no value yet, open the calendar on minDate's month instead of maxDate's
  // (a "pick a date in this window" calendar). Off by default so existing callers are unaffected.
  openToMinDate?: boolean;
  // Language for the localized weekday headers when the route has no :language param.
  language?: string;
  theme?: "care-plus";
};

const DatePickerWrapper: React.FC<DateInputProps> = ({
  onChange,
  id,
  errorMode = false,
  value,
  isBirthdayInput = true,
  inline = false,
  isBodyLevelPortal = true,
  customDayClassName,
  variant = "default",
  minDate,
  maxDate,
  filterDate,
  disabled = false,
  placeholderText,
  localizeWeekdays = false,
  openToMinDate = false,
  language: languageProp,
  theme,
}) => {
  const { language: routeLanguage } = useParams();
  const language = languageProp ?? routeLanguage;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isYearMenuOpen, setIsYearMenuOpen] = useState(false);

  /* Helpers */
  function getOptionsRange(startRange: number, finishRange: number) {
    let options: number[] = [];
    for (let i = startRange; i <= finishRange; i++) {
      options.push(i);
    }
    return options;
  }

  function getMaxDate() {
    // Limit the pick to 18 years old
    if (isBirthdayInput) {
      const birthdayMax = new Date();
      birthdayMax.setFullYear(currentYear - 18);
      return birthdayMax;
    }
    // Non-birthday inputs are unbounded unless the caller supplies an upper bound (e.g. a
    // questionnaire question about the past, which must not allow future dates).
    return maxDate ?? null;
  }

  function getMinDate() {
    if (isBirthdayInput) {
      return new Date(MIN_BIRTH_YEAR, 0, 1);
    }
    return minDate || undefined;
  }

  function getMonthIndex(date: Date): number {
    const monthIndex: number = date.getMonth();
    return monthIndex;
  }

  function getYear(date: Date): number {
    const year: number = date.getFullYear();
    return year;
  }

  /* End of Helpers */

  const initialDate = new Date();
  // Get the current year
  const currentYear = initialDate.getFullYear();
  const MIN_BIRTH_YEAR = 1930;
  // Cap years by maxDate (birthday → today − 18y). Newest first so the native
  // <select> menu opens on allowed years near the top instead of 1930 / off-screen.
  const maxYear = getMaxDate()?.getFullYear() ?? currentYear;
  const years = getOptionsRange(MIN_BIRTH_YEAR, maxYear).reverse();
  // Generate the range of months by language
  const months = timestampFormatter.getMonthsOptionsByLanguage(language!);

  // Determine the wrapper class based on variant
  const getWrapperClass = () => {
    if (variant === "appointment") {
      return "date-picker-appointment-wrapper";
    }
    return "";
  };

  // Auto-insert the dd/MM/yyyy slashes while the user types digits, so the
  // separators appear without having to press enter / blur the input.
  function handleRawChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (typeof event?.target?.value !== "string") {
      return;
    }

    const digits = event.target.value.replace(/\D/g, "").slice(0, 8);
    const segments: string[] = [];
    if (digits.length > 0) {
      segments.push(digits.slice(0, 2));
    }
    if (digits.length > 2) {
      segments.push(digits.slice(2, 4));
    }
    if (digits.length > 4) {
      segments.push(digits.slice(4, 8));
    }

    event.target.value = segments.join("/");

    if (isInvalidTypedDate(digits)) {
      onChange("");
    }
  }

  // A typed date is invalid when the day/month is out of range, or once all 8
  // digits are present the dd/MM/yyyy combination is not a real calendar date.
  function isInvalidTypedDate(digits: string) {
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);

    if (day.length === 2 && (Number(day) < 1 || Number(day) > 31)) {
      return true;
    }
    if (month.length === 2 && (Number(month) < 1 || Number(month) > 12)) {
      return true;
    }
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      const d = Number(day);
      const m = Number(month);
      const y = Number(year);
      const date = new Date(y, m - 1, d);
      return !(
        date.getFullYear() === y &&
        date.getMonth() === m - 1 &&
        date.getDate() === d
      );
    }
    return false;
  }

  function isMobileViewport() {
    return (
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
    );
  }

  function handleInputFocus(event: React.FocusEvent<HTMLInputElement>) {
    if (inline) {
      return;
    }

    setIsCalendarOpen(true);

    if (isMobileViewport()) {
      const input = event.target;
      // Wait for the keyboard + calendar to appear before repositioning.
      setTimeout(() => {
        input.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }

  function handleDateChange(date: Date) {
    if (date && (date.getFullYear() < 1000 || (isBirthdayInput && date.getFullYear() < MIN_BIRTH_YEAR))) {
      return;
    }

    const localDate = date
      ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      : null;

    onChange(localDate?.toISOString().split("T")[0]);

    if (!inline) {
      setIsCalendarOpen(false);
    }
  }

  const resolvedMaxDate = getMaxDate();
  // Prefer a date within bounds so the calendar never opens past maxDate
  // (e.g. birthday still defaulting near "today" before a value is chosen).
  const selectedDate = value ? new Date(value) : null;
  const openToDate =
    selectedDate &&
    (!resolvedMaxDate || selectedDate.getTime() <= resolvedMaxDate.getTime())
      ? selectedDate
      : (openToMinDate && !selectedDate && minDate) || resolvedMaxDate || undefined;

  const datePicker = (
    <DatePicker
      className={`${errorMode ? "date-picker-error" : "date-picker-input"} notranslate ${getWrapperClass()}`}
      customInput={<input type="text" inputMode="numeric" />}
      id={id}
      selected={selectedDate}
      openToDate={openToDate}
      onChange={handleDateChange}
      onChangeRaw={handleRawChange}
      onFocus={handleInputFocus}
      placeholderText={
        placeholderText ??
        translator.translate(
          language,
          "default",
          "oauth/additionalinfomodal/date_of_birth_placeholder",
        )
      }
      dateFormat="dd/MM/yyyy"
      calendarStartDay={1}
      disabled={disabled}
      {...(localizeWeekdays && SUPPORTED_CALENDAR_LOCALES.includes(language!)
        ? { locale: language! }
        : {})}
      // Body-portaled by default, so the theme class must sit on the calendar, not the wrapper.
      // react-datepicker renders the weekday row itself; `notranslate` keeps
      // Google Translate from garbling the abbreviations.
      calendarClassName={
        [localizeWeekdays && "notranslate", theme === "care-plus" && "date-picker-care-plus"]
          .filter(Boolean)
          .join(" ") || undefined
      }
      maxDate={resolvedMaxDate}
      minDate={getMinDate()}
      filterDate={filterDate}
      inline={inline}
      preventOpenOnFocus={!inline}
      // Renders the calendar popper into a body-level portal instead of
      // inline, so it isn't clipped by ancestors with overflow:hidden (e.g.
      // the VariantB QuestionFrame card).
      portalId={(isBodyLevelPortal && !inline) ? "date-picker-portal" : undefined }
      open={inline ? undefined : isCalendarOpen}
      onClickOutside={() => {
        setIsCalendarOpen(false);
        setIsYearMenuOpen(false);
      }}
      onCalendarClose={() => setIsYearMenuOpen(false)}
      onKeyDown={(event: React.KeyboardEvent) => {
        // `open` is controlled, so react-datepicker's own Escape handling
        // can't close the calendar - close it here.
        if (event.key === "Escape") {
          if (isYearMenuOpen) {
            setIsYearMenuOpen(false);
            return;
          }
          setIsCalendarOpen(false);
        }
      }}
      dayClassName={(date: Date) =>
        customDayClassName && customDayClassName(date)
      } // Use custom day className if provided
      // @ts-ignore
      renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
        // Header container
        <div
          className={`date-picker-header notranslate ${variant === "appointment" ? "date-picker-header-appointment" : ""}`}
        >
          <NavArrow
            direction="prev"
            variant={variant}
            disabled={prevMonthButtonDisabled}
            onClick={() => {
              if (!prevMonthButtonDisabled) {
                setIsYearMenuOpen(false);
                decreaseMonth();
              }
            }}
          />

          {/* Year select  */}
          {variant === "appointment" ? (
            <div className="date-picker-appointment-header-text">
              {months[getMonthIndex(date)]} {getYear(date)}
            </div>
          ) : (
            <>
              {/* Custom year menu (fixed height + scroll, opens downward). */}
              <div className="date-picker-year-menu">
                <button
                  type="button"
                  className="date-picker-year-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={isYearMenuOpen}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsYearMenuOpen((open) => !open);
                  }}
                >
                  {getYear(date)}
                </button>
                {isYearMenuOpen && (
                  <ul
                    className="date-picker-year-list"
                    role="listbox"
                    aria-label="Year"
                  >
                    {years.map((option) => (
                      <li key={option} role="option" aria-selected={option === getYear(date)}>
                        <button
                          type="button"
                          className={`date-picker-year-option${
                            option === getYear(date) ? " is-selected" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            changeYear(option);
                            setIsYearMenuOpen(false);
                          }}
                        >
                          {option}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <select
                value={months[getMonthIndex(date)]}
                onChange={({ target: { value } }) => {
                  setIsYearMenuOpen(false);
                  changeMonth(months.indexOf(value));
                }}
              >
                {months.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </>
          )}

          <NavArrow
            direction="next"
            variant={variant}
            disabled={nextMonthButtonDisabled}
            onClick={() => {
              if (!nextMonthButtonDisabled) {
                setIsYearMenuOpen(false);
                increaseMonth();
              }
            }}
          />
        </div>
      )}
    />
  );

  return (
    <div
      className={`date-picker-wrapper ${getWrapperClass()} ${
        theme === "care-plus" ? `date-picker-care-plus-field ${isCalendarOpen ? "date-picker-care-plus-open" : ""}` : ""
      }`}
    >
      {inline ? (
        datePicker
      ) : (
        <div className="date-picker-input-with-icon">
          {datePicker}
          <button
            type="button"
            className="date-picker-calendar-button"
            onClick={() => setIsCalendarOpen((prev) => !prev)}
            disabled={disabled}
            aria-label="Open calendar"
          >
            <LuCalendar
              className="date-picker-calendar-icon"
              aria-hidden="true"
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default DatePickerWrapper;
